import { Dialog } from '@material-ui/core';
import { ArrowBack, ArrowForward, Close } from '@material-ui/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

// "Jobs to do": one job at a time, done right here in the dialog. Each guide
// the PIP found in reviews asks one question ("Is Marco one of your
// guides?") with three answers, and the chosen answer opens its own small
// form. Closing it, however it is closed, means "remind me tomorrow".

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1 border-0 cursor-pointer font-semibold text-sm px-4 py-2 rounded-xl whitespace-nowrap transition-colors duration-150 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed';
const PRIMARY = `${BUTTON_BASE} bg-indigo-600 hover:bg-indigo-700 text-white`;
const SECONDARY = `${BUTTON_BASE} bg-white hover:bg-slate-50 text-slate-700 border border-solid border-slate-300 shadow-sm`;
const GHOST = `${BUTTON_BASE} bg-transparent hover:bg-slate-100 text-slate-600 px-3`;
const DANGER = `${BUTTON_BASE} bg-rose-600 hover:bg-rose-700 text-white`;
const INPUT =
  'w-full box-border m-0 px-3 py-2.5 text-sm text-slate-900 bg-white border border-solid border-slate-300 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200';
const LABEL = 'block text-sm font-semibold text-slate-800 mb-1.5';
const HELP = 'text-xs text-slate-500 m-0 mt-1.5';

const plural = (n, one, many) => (n === 1 ? one : many);

/** "Old Town Walk", "Old Town Walk and Food Tour", "A, B and 2 more". */
export const listTitles = titles => {
  const t = titles.filter(Boolean);
  if (t.length <= 1) return t[0] || '';
  if (t.length === 2) return `${t[0]} and ${t[1]}`;
  if (t.length === 3) return `${t[0]}, ${t[1]} and ${t[2]}`;
  return `${t[0]}, ${t[1]} and ${t.length - 2} more`;
};

const InlineError = ({ message }) =>
  message ? (
    <p role="alert" className="text-sm text-rose-700 font-semibold m-0 mt-2">
      {message}
    </p>
  ) : null;

InlineError.propTypes = { message: PropTypes.string };

const Skeleton = () => (
  <div className="p-6 flex flex-col gap-4 animate-pulse" role="status" aria-label="Loading your jobs">
    <div className="h-5 w-2/3 bg-slate-100 rounded" />
    <div className="h-3 w-full bg-slate-100 rounded" />
    <div className="h-3 w-5/6 bg-slate-100 rounded" />
    <div className="flex gap-2 mt-2">
      <div className="h-9 w-36 bg-slate-100 rounded-xl" />
      <div className="h-9 w-36 bg-slate-100 rounded-xl" />
    </div>
  </div>
);

/** Runs an action, holding the buttons while it's in flight and showing why it failed. */
const useAction = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A finished job leaves the list, unmounting this form mid-await.
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);
  const run = async fn => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      if (mounted.current) setError(err?.message || 'That did not save. Try again.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return { busy, error, run, setError };
};

export function GuideJob({ job, knownGuides, onInvite, onLink, onDismiss }) {
  const { guide, products, suggestedTourIds, previousInvite } = job;
  const first = guide.firstName;
  const [mode, setMode] = useState('ask');
  const [email, setEmail] = useState(previousInvite?.email || '');
  const [tourIds, setTourIds] = useState(suggestedTourIds);
  const [target, setTarget] = useState('');
  const { busy, error, run, setError } = useAction();

  // Two reviewed products can share one TipDirect tour: list it once.
  const tours = products.filter((p, i) => p.tourId && products.findIndex(q => q.tourId === p.tourId) === i);
  const linkTargets = (knownGuides || []).filter(g => g.id !== job.id);
  const onTipDirect = linkTargets.filter(g => g.onTipDirect);
  const alsoFound = linkTargets.filter(g => !g.onTipDirect);
  const productTitles = listTitles(products.map(p => p.title));

  const go = next => {
    setError(null);
    setMode(next);
  };

  const toggleTour = id =>
    setTourIds(prev => (prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]));

  return (
    <div data-admin-job={job.id} data-admin-job-type="pip_guide_invite">
      <h3 className="text-lg font-black text-slate-900 m-0">Is {guide.displayName} one of your guides?</h3>
      <p className="text-sm text-slate-600 leading-relaxed m-0 mt-2">
        Guests named {first} in {guide.reviewMentions} {plural(guide.reviewMentions, 'review', 'reviews')}
        {productTitles ? ` of ${productTitles}` : ''}. {first} isn&apos;t on TipDirect yet.
      </p>
      {guide.namesInReviews.length > 1 && (
        <p className="text-xs text-slate-500 m-0 mt-1.5">Guests wrote: {guide.namesInReviews.join(', ')}</p>
      )}
      {previousInvite && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-solid border-amber-200 rounded-xl px-3 py-2 m-0 mt-3">
          Your invite to {previousInvite.email} wasn&apos;t accepted in time. You can send another.
        </p>
      )}

      {mode === 'ask' && (
        <div className="flex flex-wrap gap-2 mt-5">
          <button type="button" className={PRIMARY} onClick={() => go('invite')}>
            Yes, invite {first}
          </button>
          {linkTargets.length > 0 && (
            <button type="button" className={SECONDARY} onClick={() => go('link')}>
              {onTipDirect.length > 0 ? 'Already on TipDirect' : 'Same as another guide'}
            </button>
          )}
          <button type="button" className={GHOST} onClick={() => go('dismiss')}>
            Not our guide
          </button>
        </div>
      )}

      {mode === 'invite' && (
        <form
          className="mt-5"
          onSubmit={event => {
            event.preventDefault();
            run(() => onInvite(job, { email: email.trim(), tourIds }));
          }}>
          <label className={LABEL} htmlFor={`invite-email-${job.id}`}>
            {first}&apos;s email address
          </label>
          <input
            id={`invite-email-${job.id}`}
            type="email"
            className={INPUT}
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            required
          />
          <p className={HELP}>We&apos;ll send {first} one email inviting them to join your team on TipDirect.</p>

          {tours.length > 0 ? (
            <fieldset className="border-0 p-0 m-0 mt-4">
              <legend className={`${LABEL} p-0`}>Add their tours to their profile</legend>
              {tours.map(p => (
                <label key={p.tourId} className="flex items-start gap-2 text-sm text-slate-700 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-indigo-600"
                    checked={tourIds.includes(p.tourId)}
                    onChange={() => toggleTour(p.tourId)}
                  />
                  <span>
                    {p.tourName}
                    <span className="text-slate-400"> (reviewed as {p.title})</span>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : (
            <p className="text-xs text-slate-500 m-0 mt-4">
              None of these reviews are on a tour you&apos;ve linked in Product Improvement, so there are no tours to
              add yet. You can add tours from Users once {first} joins.
            </p>
          )}

          <InlineError message={error} />
          <div className="flex flex-wrap gap-2 mt-5">
            <button type="submit" className={PRIMARY} disabled={busy || !email.trim()}>
              {busy ? 'Sending' : 'Send invite'}
            </button>
            <button type="button" className={SECONDARY} disabled={busy} onClick={() => go('ask')}>
              Back
            </button>
          </div>
        </form>
      )}

      {mode === 'link' && (
        <form
          className="mt-5"
          onSubmit={event => {
            event.preventDefault();
            run(() => onLink(job, target));
          }}>
          <label className={LABEL} htmlFor={`link-guide-${job.id}`}>
            Which guide is {first}?
          </label>
          <select
            id={`link-guide-${job.id}`}
            className={INPUT}
            value={target}
            onChange={e => setTarget(e.target.value)}
            required>
            <option value="">Choose a guide</option>
            {onTipDirect.length > 0 && (
              <optgroup label="On TipDirect">
                {onTipDirect.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </optgroup>
            )}
            {alsoFound.length > 0 && (
              <optgroup label="Also found in your reviews">
                {alsoFound.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <p className={HELP}>Reviews that name {first} will count for the guide you pick.</p>
          <InlineError message={error} />
          <div className="flex flex-wrap gap-2 mt-5">
            <button type="submit" className={PRIMARY} disabled={busy || !target}>
              {busy ? 'Linking' : 'Link guide'}
            </button>
            <button type="button" className={SECONDARY} disabled={busy} onClick={() => go('ask')}>
              Back
            </button>
          </div>
        </form>
      )}

      {mode === 'dismiss' && (
        <div className="mt-5">
          <p className="text-sm text-slate-700 m-0">
            We won&apos;t suggest {first} again, and reviews naming {first} won&apos;t count for any of your guides.
          </p>
          <InlineError message={error} />
          <div className="flex flex-wrap gap-2 mt-5">
            <button type="button" className={DANGER} disabled={busy} onClick={() => run(() => onDismiss(job))}>
              {busy ? 'Saving' : 'Not our guide'}
            </button>
            <button type="button" className={SECONDARY} disabled={busy} onClick={() => go('ask')}>
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

GuideJob.propTypes = {
  job: PropTypes.object.isRequired,
  knownGuides: PropTypes.array,
  onInvite: PropTypes.func.isRequired,
  onLink: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired
};

export const TieBreakJob = ({ job, onOpen }) => (
  <div data-admin-job={job.id} data-admin-job-type="pip_guide_tiebreak">
    <h3 className="text-lg font-black text-slate-900 m-0">Which guide was it?</h3>
    <p className="text-sm text-slate-600 leading-relaxed m-0 mt-2">
      {job.count === 1
        ? 'One review mentions a name that matches more than one of your guides.'
        : `${job.count} reviews mention a name that matches more than one of your guides.`}{' '}
      Pick who it was so their ratings count.
    </p>
    <div className="flex flex-wrap gap-2 mt-5">
      <button type="button" className={PRIMARY} onClick={onOpen}>
        Open Guide Intelligence
      </button>
    </div>
  </div>
);

TieBreakJob.propTypes = { job: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };

export function AdminJobsDialog({
  open,
  loading,
  error,
  data,
  focusId,
  notice,
  onClose,
  onRetry,
  onInvite,
  onLink,
  onDismiss,
  onOpenTieBreaks
}) {
  const jobs = data?.jobs || [];
  const [index, setIndex] = useState(0);

  // Open at the job asked for (the PIP's "Invite" button), else the first.
  useEffect(() => {
    if (!open) return;
    const at = focusId ? jobs.findIndex(j => j.id === focusId) : -1;
    setIndex(at >= 0 ? at : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusId]);

  // A finished job drops out of the list; stay at the same place in it.
  useEffect(() => {
    if (index > 0 && index >= jobs.length) setIndex(Math.max(0, jobs.length - 1));
  }, [jobs.length, index]);

  const job = jobs[Math.min(index, jobs.length - 1)];
  const isLast = index >= jobs.length - 1;
  const subtitle =
    jobs.length === 1 ? 'One thing needs you' : `${jobs.length} things need you`;

  // As in What's new: an onKeyDown on an MUI v4 Dialog replaces the Modal's
  // own Escape handling, so Escape is handled here.
  const onKeyDown = event => {
    if (event.key === 'Escape') onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={onKeyDown}
      maxWidth="sm"
      fullWidth
      aria-labelledby="admin-jobs-title"
      PaperProps={{ style: { borderRadius: 24 }, 'data-testid': 'admin-jobs-dialog' }}>
      <div className="flex items-start justify-between gap-4 px-6 pt-5">
        <div className="min-w-0">
          <h2 id="admin-jobs-title" className="text-xl font-black text-slate-900 m-0">
            Jobs to do
          </h2>
          {!loading && !error && jobs.length > 0 && <p className="text-sm text-slate-500 m-0 mt-1">{subtitle}</p>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className={`${GHOST} !p-2 -mr-2`}>
          <Close fontSize="small" />
        </button>
      </div>

      {notice && (
        <p
          role="status"
          className="mx-6 mt-4 mb-0 text-sm font-semibold text-emerald-800 bg-emerald-50 border border-solid border-emerald-200 rounded-xl px-3 py-2">
          {notice}
        </p>
      )}

      {loading && !data && <Skeleton />}

      {!loading && error && (
        <div className="px-6 py-8 flex flex-col items-start gap-3">
          <p className="text-sm text-slate-600 m-0">We couldn&apos;t load your jobs.</p>
          <button type="button" className={SECONDARY} onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      {!error && data && jobs.length === 0 && (
        <div className="px-6 py-8 flex flex-col items-start gap-3">
          <p className="text-base font-bold text-slate-900 m-0">That&apos;s everything</p>
          <p className="text-sm text-slate-500 m-0">Nothing else needs you right now.</p>
          <button type="button" className={SECONDARY} onClick={onClose}>
            Close
          </button>
        </div>
      )}

      {!error && job && (
        <>
          <div className="px-6 pt-5" key={job.id}>
            {job.type === 'pip_guide_tiebreak' ? (
              <TieBreakJob job={job} onOpen={onOpenTieBreaks} />
            ) : (
              <GuideJob
                job={job}
                knownGuides={data.knownGuides}
                onInvite={onInvite}
                onLink={onLink}
                onDismiss={onDismiss}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-3 px-6 py-5 mt-4 border-0 border-t border-solid border-slate-100 flex-wrap">
            <span className="text-xs text-slate-400 font-semibold" aria-live="polite">
              {jobs.length > 1 ? `${Math.min(index, jobs.length - 1) + 1} of ${jobs.length}` : ''}
            </span>
            <div className="flex items-center gap-2">
              <button type="button" className={GHOST} onClick={onClose}>
                Remind me tomorrow
              </button>
              {jobs.length > 1 && (
                <>
                  <button
                    type="button"
                    className={`${SECONDARY} !px-2`}
                    aria-label="Previous job"
                    disabled={index === 0}
                    onClick={() => setIndex(i => Math.max(0, i - 1))}>
                    <ArrowBack fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className={`${SECONDARY} !px-2`}
                    aria-label="Next job"
                    disabled={isLast}
                    onClick={() => setIndex(i => Math.min(jobs.length - 1, i + 1))}>
                    <ArrowForward fontSize="small" />
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}

AdminJobsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  data: PropTypes.object,
  focusId: PropTypes.string,
  notice: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onInvite: PropTypes.func.isRequired,
  onLink: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onOpenTieBreaks: PropTypes.func.isRequired
};

export default AdminJobsDialog;
